import { useTranslation } from "react-i18next";
import { Calculator, Play, Sparkles, ShieldCheck, BarChart3, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import SmartImage from "@/components/ui/smart-image";

export default function HeroSection() {
  const { t } = useTranslation();
  const handleRedirect = () => {
    window.open("https://www.realtyblocks.com", "_blank");
  };

  const trustItems = [
    { Icon: ShieldCheck, label: "RERA-aware" },
    { Icon: BarChart3, label: "Live market data" },
    { Icon: Bot, label: "AI-powered" },
  ];

  return (
    <section
      id="main-content"
      className="relative overflow-hidden pt-32 sm:pt-44 pb-16 sm:pb-20 bg-background"
    >
      {/* Animated gradient mesh background */}
      <div className="absolute inset-0 -z-10 gradient-mesh" aria-hidden="true" />
      <div
        className="absolute inset-x-0 top-0 -z-10 h-[480px] opacity-60"
        aria-hidden="true"
      >
        <SmartImage
          src="/assets/images/Modern_real_estate_cityscape_c4ca7fef.png"
          alt=""
          className="w-full h-full object-cover opacity-[0.08] parallax-element"
          width={1920}
          height={1080}
          loading="eager"
          decoding="async"
          sizes="100vw"
          widths={[800, 1200, 1600, 1920]}
          {...({ fetchpriority: "high" } as Record<string, string>)}
        />
      </div>

      {/* Decorative blobs */}
      <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-accent/10 blur-3xl -z-10" aria-hidden="true" />
      <div className="absolute top-40 -left-20 w-[24rem] h-[24rem] rounded-full bg-primary/10 blur-3xl -z-10" aria-hidden="true" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center">
        <div className="fade-in">
          <div className="inline-flex items-center gap-2 trust-pill mb-6">
            <Sparkles className="h-3.5 w-3.5" />
            India's most accurate AI valuation
          </div>

          <h1 className="font-display text-display-2xl text-foreground mb-5">
            {t("hero.titleLine1")}{" "}
            <span className="gradient-text">{t("hero.titleHighlight")}</span>{" "}
            {t("hero.titleLine2")}
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
            {t("hero.subtitle")}
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 mb-8">
            <a href="#valuation" className="w-full sm:w-auto">
              <Button
                variant="accent"
                size="xl"
                className="w-full sm:w-auto group"
                data-testid="button-free-valuation"
              >
                <Calculator className="h-5 w-5" />
                {t("hero.ctaPrimary")}
                <span className="pulse-ring absolute -right-1 -top-1 h-2 w-2 rounded-full bg-accent" />
              </Button>
            </a>
            <Button
              onClick={handleRedirect}
              variant="outline"
              size="xl"
              className="w-full sm:w-auto"
              data-testid="button-discover-realtyblocks"
            >
              <Play className="h-4 w-4" />
              {t("hero.ctaSecondary")}
            </Button>
          </div>

          {/* Trust strip */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-12">
            {trustItems.map(({ Icon, label }) => (
              <div
                key={label}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card border border-border text-xs sm:text-sm text-muted-foreground shadow-xs"
              >
                <Icon className="h-3.5 w-3.5 text-primary" />
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Property triptych */}
        <div className="fade-in relative z-20" style={{ animationDelay: "0.25s" }}>
          <div className="surface-elevated p-4 sm:p-6 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
              {[
                { src: "/assets/images/Modern_apartment_interior_beb39e27.png", title: t("hero.property1Title"), loc: t("hero.property1Location"), price: "₹1.2 Cr", match: 95 },
                { src: "/assets/images/Luxury_villa_exterior_5b9a0e7c.png",       title: t("hero.property2Title"), loc: t("hero.property2Location"), price: "₹2.8 Cr", match: 88 },
                { src: "/assets/images/Contemporary_residential_home_fbcb2285.png",title: t("hero.property3Title"), loc: t("hero.property3Location"), price: "₹95 L",  match: 92 },
              ].map((p, i) => (
                <button
                  key={i}
                  onClick={handleRedirect}
                  className="group text-left rounded-xl overflow-hidden border border-border bg-card hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ease-emphasized"
                  data-testid={`card-property-${i + 1}`}
                >
                  <div className="relative overflow-hidden">
                    <SmartImage
                      src={p.src}
                      alt={p.title}
                      width={600}
                      height={400}
                      sizes="(max-width: 768px) 100vw, 33vw"
                      widths={[400, 600, 800, 1200]}
                      className="w-full h-44 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-500 ease-emphasized"
                    />
                    <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-card/90 backdrop-blur text-xs font-semibold text-primary inline-flex items-center gap-1">
                      <Bot className="h-3 w-3" /> AI {p.match}%
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-display font-semibold text-foreground mb-1">{p.title}</h3>
                    <p className="text-muted-foreground text-sm mb-2">{p.loc}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-primary font-bold text-lg">{p.price}</span>
                      <span className="text-xs text-muted-foreground">{t("hero.aiMatch")}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
